import {
  NewEventTemplate,
  BareEventPropertyInfo,
  BareEventTemplate,
  EventTemplate,
  DefaultEventDisplayOption,
} from '../types/eventTemplate';
import { Brand, BrandLevel, BrandAttribute, BrandSegment } from '../types/brand';
import { searchById } from './functional';
import { ResourceNotFoundError } from '../errors';
import { EventTemplateTranslation, Translated } from '../../shared/types/translation';
import { hasOwnProperty } from './utils';

export const EVENT_LIBRARY_COLUMNS: string[] = [
  'Title',
  'Description',
  'Segment',
  'Levels',
  'Attributes',
  'Private',
  'Crew',
  'Simple',
  'Duration',
];

interface DefaultNewEventTemplateParams {
  brandId: string;
  title: string;
  source: string;
  properties: BareEventPropertyInfo[];
}

export const generateDefaultNewEventTemplate = ({
  title,
  brandId,
  source,
  properties,
}: DefaultNewEventTemplateParams): NewEventTemplate => {
  return {
    brand: brandId,
    title,
    source,
    description: '',
    levels: [],

    attributes: [],
    incomplete: true,
    defaultDuration: 60,
    properties,
    images: [],
    softConflict: false,
    isPrivate: false,
    crewOnly: false,
    interactiveFeedDisplay: DefaultEventDisplayOption,
    callToActions: [],
  };
};

/**
 * Given a bare event template and a brand, populate the reference fields from
 * the brand values
 *
 */
export function populateEventTemplate(
  bareTemplate: BareEventTemplate,
  brand: Brand
): EventTemplate {
  // Ensure all referenced values exist, otherwise throw an error
  let segment: BrandSegment | undefined;
  if (bareTemplate.segment) {
    const match = searchById(bareTemplate.segment, brand.segments);
    if (!match) {
      throw new ResourceNotFoundError(`Cannot find referenced segment ${bareTemplate.segment}`);
    }

    segment = match;
  }
  let levels: BrandLevel[] = [];
  bareTemplate.levels.forEach(levelId => {
    let level = searchById(levelId, brand.levels);
    if (!level) throw new ResourceNotFoundError(`Cannot find referenced level ${levelId}`);
    levels.push(level);
  });
  let attributes: BrandAttribute[] = [];
  bareTemplate.attributes.forEach(attributeId => {
    let attribute = searchById(attributeId, brand.attributes);
    if (!attribute) {
      throw new ResourceNotFoundError(`Cannot find referenced attribute ${attributeId}`);
    }
    attributes.push(attribute);
  });

  return {
    ...bareTemplate,
    segment,
    levels,
    attributes,
    brand,
  };
}

export function translateEventTemplate(
  eventTemplate: BareEventTemplate,
  translation?: EventTemplateTranslation,
  lang?: string
): Translated<BareEventTemplate> | undefined;
export function translateEventTemplate(
  eventTemplate: EventTemplate,
  translation?: EventTemplateTranslation,
  lang?: string
): Translated<EventTemplate> | undefined;
export function translateEventTemplate(
  eventTemplate: BareEventTemplate | EventTemplate,
  translation?: EventTemplateTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...eventTemplate,
    title: transforms[lang].delta.title,
    description: transforms[lang].delta.description,
    _lang: lang,
  };
}
